import React from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { grey, red } from "@mui/material/colors";

import GrowthPercentage from "../../../ReusableComponents/GrowthPercentage";
import NumberTextField from "../../../ReusableComponents/NumberTextField";
import { getSeasonMonths, round1dp } from "../../data";
import Tooltip from "@mui/material/Tooltip";

const growthPercent = (cur, base) => {
	if (!base) return 0;
	return round1dp(((cur - base) / base) * 100);
};

const toLakhs = (nsv) => round1dp((nsv || 0) / 100000);

const monthNsv = (rows, monthKey) => {
	const row = (rows || []).find((ml) => ml.month === monthKey);
	return toLakhs(row?.nsv);
};

const totalLakhs = (rows) => toLakhs((rows || []).reduce((s, m) => s + (m.nsv || 0), 0));

const getRepeatStats = (rows, monthKey = null) => {
	let rep = 0;
	let neu = 0;
	if (monthKey != null) {
		const row = (rows || []).find((ml) => ml.month === monthKey);
		rep = Number(row?.repeat_customer_count) || 0;
		neu = Number(row?.new_customer_count) || 0;
	} else {
		const list = rows || [];
		rep = list.reduce((s, m) => s + (Number(m.repeat_customer_count) || 0), 0);
		neu = list.reduce((s, m) => s + (Number(m.new_customer_count) || 0), 0);
	}
	const total = rep + neu;
	if (!total) return null;
	return {
		repeat: rep,
		neu,
		total,
		repeatPct: round1dp((rep / total) * 100),
		newPct: round1dp((neu / total) * 100),
	};
};

const totalRepeatPct = (rows) => getRepeatStats(rows)?.repeatPct ?? null;

const RepeatTooltipTitle = ({ stats }) => {
	if (!stats) return null;
	return (
		<Box sx={{ py: 0.5 }}>
			<Typography fontSize={12} fontWeight={700} mb={0.5}>
				Customer mix
			</Typography>
			<Typography fontSize={12}>Repeat: {stats.repeat.toLocaleString("en-IN")} ({stats.repeatPct}%)</Typography>
			<Typography fontSize={12}>New: {stats.neu.toLocaleString("en-IN")} ({stats.newPct}%)</Typography>
			<Typography fontSize={12} mt={0.5} color="rgba(255,255,255,0.8)">
				Total: {stats.total.toLocaleString("en-IN")}
			</Typography>
		</Box>
	);
};

const cellSx = {
	flex: 1,
	minWidth: 0,
	padding: "5px 0px",
	height: "auto",
	minHeight: 52,
	py: 0.5,
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
};

const labelCellSx = {
	...cellSx,
	flex: "0 0 130px",
	maxWidth: 140,
};

const CompactGrowth = ({ value }) => (
	<GrowthPercentage
		number={value}
		textStyle={{ fontSize: "10px", lineHeight: 1.1 }}
		sx={{
			mt: 0.15,
			minHeight: 14,
			"& .MuiSvgIcon-root": { width: 12, height: 12, transform: "scale(1)" },
		}}
	/>
);

export const MonthValueCell = ({ value, growth, repeatPct, repeatStats, children }) => (
	<Box sx={cellSx} display="flex" alignItems="center" justifyContent="center" columnGap={0.75}>
		<Stack alignItems="center" justifyContent="center" spacing={0} sx={{ minWidth: 0 }}>
			{children || (
				<Typography fontSize={16} fontWeight={500} lineHeight={1.2}>
					{value}
				</Typography>
			)}
			{growth != null && <CompactGrowth value={growth} />}
		</Stack>
		{repeatPct != null && (
			<Tooltip
				arrow
				placement="top"
				title={<RepeatTooltipTitle stats={repeatStats} />}
				slotProps={{ tooltip: { sx: { maxWidth: 260 } } }}
			>
				<Typography
					fontSize={13}
					color={grey[500]}
					fontWeight={500}
					sx={{
						whiteSpace: "nowrap",
						pl: 0.5,
						borderLeft: `1px solid ${grey[200]}`,
						lineHeight: 1.2,
						cursor: "help",
					}}
				>
					R {repeatPct}%
				</Typography>
			</Tooltip>
		)}
	</Box>
);

const RowLabel = ({ seasonLabel, fallback }) => (
	<Typography sx={{ ...labelCellSx, fontSize: 15, fontWeight: 600, color: grey[800] }} textAlign="center">
		{seasonLabel || fallback}{" "}
		<Box component="span" sx={{ fontSize: 11, fontWeight: 400, color: grey[500] }}>
			(LAKHS)
		</Box>
	</Typography>
);

const SalesProjectionTable = ({
	season,
	planSeasonLabel,
	ltlSeasonLabel,
	lltlSeasonLabel,
	monthlyLtl,
	monthlyLltl,
	monthlyPrevLltl,
	ltlKpi,
	projData,
	canEdit,
	onMonthChange,
}) => {
	const seasonMonths = getSeasonMonths(season);
	const lltlTotal = totalLakhs(monthlyLltl);
	const ltlTotal = totalLakhs(monthlyLtl);
	const prevLltlTotal = totalLakhs(monthlyPrevLltl);

	return (
		<Stack width="100%" border={`1px solid ${grey[300]}`} borderRadius="4px" overflow="hidden">
			{/* Month header — once */}
			<Box
				width="100%"
				display="flex"
				alignItems="center"
				sx={{ background: grey[100], borderBottom: `1px solid ${grey[300]}` }}
			>
				<Box sx={{ ...labelCellSx, minHeight: 34 }} />
				{seasonMonths.map((m) => (
					<Typography
						key={`hdr-${m.key}`}
						sx={{ ...cellSx, minHeight: 34 }}
						fontSize={13}
						fontWeight={600}
						textTransform="uppercase"
						color={grey[700]}
						textAlign="center"
					>
						{m.name}
					</Typography>
				))}
				<Typography
					sx={{ ...cellSx, minHeight: 34 }}
					fontSize={13}
					fontWeight={600}
					textTransform="uppercase"
					color={grey[700]}
					textAlign="center"
				>
					TOTAL
				</Typography>
			</Box>

			{/* LLTL vs Prev-LLTL */}
			<Box
				width="100%"
				display="flex"
				alignItems="stretch"
				sx={{ background: "#fafafa", borderBottom: `1px solid ${grey[200]}` }}
			>
				<RowLabel seasonLabel={lltlSeasonLabel} fallback="LLTL" />
				{seasonMonths.map((m) => {
					const cur = monthNsv(monthlyLltl, m.key);
					const base = monthNsv(monthlyPrevLltl, m.key);
					const stats = getRepeatStats(monthlyLltl, m.key);
					return (
						<MonthValueCell
							key={`lltl-${m.key}`}
							value={cur}
							growth={growthPercent(cur, base)}
							repeatPct={stats?.repeatPct ?? null}
							repeatStats={stats}
						/>
					);
				})}
				<MonthValueCell
					value={lltlTotal}
					growth={growthPercent(lltlTotal, prevLltlTotal)}
					repeatPct={totalRepeatPct(monthlyLltl)}
					repeatStats={getRepeatStats(monthlyLltl)}
				/>
			</Box>

			{/* LTL vs LLTL */}
			<Box
				width="100%"
				display="flex"
				alignItems="stretch"
				sx={{ background: "#f5f5f5", borderBottom: `1px solid ${grey[200]}` }}
			>
				<RowLabel seasonLabel={ltlSeasonLabel} fallback="LTL" />
				{seasonMonths.map((m) => {
					const cur = monthNsv(monthlyLtl, m.key);
					const base = monthNsv(monthlyLltl, m.key);
					const stats = getRepeatStats(monthlyLtl, m.key);
					return (
						<MonthValueCell
							key={`ltl-${m.key}`}
							value={cur}
							growth={growthPercent(cur, base)}
							repeatPct={stats?.repeatPct ?? null}
							repeatStats={stats}
						/>
					);
				})}
				<MonthValueCell
					value={ltlTotal}
					growth={growthPercent(ltlTotal, lltlTotal)}
					repeatPct={totalRepeatPct(monthlyLtl)}
					repeatStats={getRepeatStats(monthlyLtl)}
				/>
			</Box>

			{/* Plan season vs LTL */}
			<Box
				width="100%"
				display="flex"
				alignItems="stretch"
				sx={
					canEdit
						? { boxShadow: "1px 1px 4px lightgrey", border: `1px solid ${red[900]}`, borderRadius: "0 0 4px 4px" }
						: {}
				}
			>
				<RowLabel seasonLabel={planSeasonLabel} fallback="NEW" />
				{seasonMonths.map((m) => {
					const ltlVal = monthNsv(monthlyLtl, m.key);
					const newVal = projData[`month_${m.key}`] || 0;
					return (
						<MonthValueCell key={`new-${m.key}`} growth={growthPercent(newVal, ltlVal)}>
							{canEdit ? (
								<NumberTextField
									disableHelperText
									size="small"
									value={projData[`month_${m.key}`] || ""}
									onChange={(e) => onMonthChange(m.key, e.target.value)}
									sx={{ width: 68, "& .MuiInputBase-input": { py: 0.5, fontSize: 14 } }}
									inputProps={{ style: { textAlign: "center" } }}
								/>
							) : (
								<Typography fontSize={16} fontWeight={500} lineHeight={1.2}>
									{newVal}
								</Typography>
							)}
						</MonthValueCell>
					);
				})}
				<MonthValueCell
					value={projData.total_projection || 0}
					growth={
						ltlKpi ? growthPercent(projData.total_projection, round1dp(ltlKpi.revenue / 100000)) : 0
					}
				/>
			</Box>
		</Stack>
	);
};

export default SalesProjectionTable;
